import React, { useMemo, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import type { Todo } from '../api/types'
import { useSections } from '../api/hooks'
import { useTodoActions } from '../hooks/useTodoActions'
import { todayStr } from '../lib/parse'

export type MoveToMenuProps = {
  todo: Todo
  /** Called after a destination is chosen so the parent context menu can close. */
  onMoved: () => void
}

type MoveTarget = { id: string; label: string; onClick: () => void }

const ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  textAlign: 'left',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontFamily: 'var(--font-serif)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-ink)',
  padding: '6px 8px',
  borderRadius: 'var(--radius-sm)',
  whiteSpace: 'nowrap',
}

function hoverOn(e: React.MouseEvent<HTMLElement>) {
  e.currentTarget.style.background = 'var(--color-paper-dark)'
}
function hoverOff(e: React.MouseEvent<HTMLElement>) {
  e.currentTarget.style.background = 'transparent'
}

/**
 * "Move to ▸" row for the todo context menu. Hovering reveals a flyout listing
 * every destination: Today (only for section todos — week todos already live on
 * a day) plus all sections except the one the todo is currently in.
 */
export function MoveToMenu({ todo, onMoved }: MoveToMenuProps) {
  const { data: sections = [] } = useSections()
  const { moveToDate, moveToSection } = useTodoActions(todo)
  const [open, setOpen] = useState(false)

  // Week todos carry a date; section todos don't. Today is only an option when
  // the todo isn't already living on a day.
  const isSectionTodo = todo.date === null

  const targets = useMemo<MoveTarget[]>(() => {
    const list: MoveTarget[] = []
    if (isSectionTodo) {
      list.push({ id: '__today', label: 'Today', onClick: () => moveToDate(todayStr()) })
    }
    for (const s of [...sections].sort((a, b) => a.position - b.position)) {
      if (s.id === todo.section_id) continue
      list.push({ id: s.id, label: s.name, onClick: () => moveToSection(s.id) })
    }
    return list
  }, [isSectionTodo, sections, todo.section_id, moveToDate, moveToSection])

  const select = (t: MoveTarget) => {
    t.onClick()
    onMoved()
  }

  if (targets.length === 0) return null

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onPointerDown={(e) => e.stopPropagation()}
        style={{ ...ITEM_STYLE, justifyContent: 'space-between' }}
        onMouseEnter={hoverOn}
        onMouseLeave={hoverOff}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChevronRight size={14} strokeWidth={1.5} style={{ opacity: 0 }} />
          Move to
        </span>
        <ChevronRight size={14} strokeWidth={1.5} style={{ opacity: 0.6 }} />
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: -8,
            left: '100%',
            marginLeft: 4,
            zIndex: 1,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            padding: '8px 10px',
            minWidth: 140,
            maxWidth: 'min(240px, 60vw)',
            maxHeight: 'min(320px, 60vh)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {targets.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitem"
              onClick={() => select(t)}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                ...ITEM_STYLE,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
